import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { IconImage, IconAttach, IconRight, colors } from '@blocket/blocket-ui'
import { setIsBrowsingFiles } from 'reduxBits/chat/actions'
import { sessionStorageSetItem, sessionStorageRemoveItem } from 'utils/useSessionStorage'
import {
  Wrapper,
  IconContainer,
  UploadButton,
  IconSendButton,
  Input,
  TextArea,
  IconSendFill,
  InnerIconWrapper,
  TextAreaWrapper,
} from './style.js'
import { onEnterKeyEvent } from 'utils/events'
import useOnMount from 'customHooks/useOnMount'
import { scrollToBottom } from '../../../utils/events/scroll'
import {
  supportedMimeTypesImg,
  supportedMimeTypesFile,
} from '../../../constants/supportedFileTypes'
import useDetectBrowserTabFocus from '../../../customHooks/useDetectBrowserTabFocus.js'

const AttachmentIcons = ({
  handleUploadRefs,
  onUploadFileChange,
  fileUploaderForwardRef,
  imageUploaderForwardRef,
}) => {
  const dispatch = useDispatch()
  const attachmentArray = ['file', 'image']
  const attachmentIcons = attachmentArray.map((type) => {
    const isFile = type === 'file'
    return (
      <React.Fragment key={type}>
        <UploadButton
          htmlFor={`${type}Input`}
          name={type}
          tabIndex="0"
          role="button"
          onKeyDown={(e) => onEnterKeyEvent(e, handleUploadRefs, type)}
          onClick={(e) => handleUploadRefs(e, type)}
          aria-label={`Ladda upp ${isFile ? 'filer' : 'bilder'}.`}
        >
          {isFile ? (
            <IconAttach height={26} color={colors.grey700} aria-hidden="true" />
          ) : (
            <IconImage height={26} color={colors.grey700} aria-hidden="true" />
          )}
        </UploadButton>
        <Input
          id={`${type}Input`}
          name={type}
          data-testid={`${type}Input`}
          type="file"
          multiple="multiple"
          value=""
          onChange={(e) => onUploadFileChange(type, e)}
          onClick={() => dispatch(setIsBrowsingFiles(true))}
          ref={isFile ? fileUploaderForwardRef : imageUploaderForwardRef}
          accept={isFile ? supportedMimeTypesFile : supportedMimeTypesImg}
        />
      </React.Fragment>
    )
  })
  return attachmentIcons
}

export const InputField = ({
  input,
  validateMessage,
  handleUploadRefs,
  onUploadFileChange,
  fileUploaderForwardRef,
  imageUploaderForwardRef,
  textInputForwardRef,
  selectedChannel,
  setInput,
  sessionStorageKey,
  previewImages,
  isMobile,
  isSending,
}) => {
  const { isBrowsingFiles } = useSelector((state) => state.chat)
  const dispatch = useDispatch()
  const previewingImages = previewImages?.length > 0
  const inputFieldHasText = input.trim() !== ''
  const showSendButton = inputFieldHasText || previewingImages
  const tabFocus = useDetectBrowserTabFocus()
  const [showAttachmentIcons, setShowAttachmentIcons] = useState(true)
  const [focus, setFocus] = useState(false)

  const onInputChange = useCallback(
    (e) => {
      const { value } = e.target
      const trimValueLength = value.trim().length

      if (trimValueLength === 0) {
        sessionStorageRemoveItem(sessionStorageKey)
      } else {
        sessionStorageSetItem(sessionStorageKey, value)
      }
      setInput(value)
    },
    [sessionStorageKey, setInput],
  )

  const autoExpand = useCallback((e = {}) => {
    const { target } = e
    target.style.height = 'inherit'
    const maxHeight = 156
    const height = target.scrollHeight
    const formatHeight = (height) => `${height}px`
    target.style.height = height < maxHeight ? formatHeight(height) : formatHeight(maxHeight)
  }, [])

  const inputAction = useCallback(
    (event = {}) => {
      if (event.target.value) {
        selectedChannel.startTyping()
      } else {
        selectedChannel.endTyping()
      }
    },
    [selectedChannel],
  )

  useEffect(() => {
    const currentInputField = textInputForwardRef.current

    if (input === '') {
      currentInputField.style.height = 'inherit'
    }
    // scroll input element to bottom if not active/blured
    if ((currentInputField !== document.activeElement && input) || (!focus && input)) {
      scrollToBottom(currentInputField)
    }
  }, [textInputForwardRef, focus, input])

  useOnMount(() => {
    textInputForwardRef.current.style.height = 'inherit'
  })

  useEffect(() => {
    // reset isBrowsingFiles when returning from browsing files
    if (isBrowsingFiles) {
      document.body.onfocus = () => {
        // we need to wait for tabFocus which is set to false when using the camera on Andriod.
        // if 'isBrowsingFiles' is set to false before tabFocus is true the sb.disconnect will be triggered..
        if (tabFocus) {
          dispatch(setIsBrowsingFiles(false))
        }
      }
    }
  }, [dispatch, isBrowsingFiles, tabFocus])

  return (
    <Wrapper data-testid="input-field" mobileView={isMobile && focus}>
      {showAttachmentIcons && (
        <InnerIconWrapper data-testid="attachment-icons">
          <AttachmentIcons
            onUploadFileChange={onUploadFileChange}
            handleUploadRefs={handleUploadRefs}
            fileUploaderForwardRef={fileUploaderForwardRef}
            imageUploaderForwardRef={imageUploaderForwardRef}
          />
        </InnerIconWrapper>
      )}
      {isMobile && focus && (
        <InnerIconWrapper data-testid="icon-right">
          <IconRight height={24} onClick={() => setVisible(true)} />
        </InnerIconWrapper>
      )}
      <TextAreaWrapper>
        <TextArea
          ref={textInputForwardRef}
          name="message-input"
          data-testid="text-input"
          type="text"
          value={input}
          onChange={onInputChange}
          onInput={(e) => {
            inputAction(e)
            setTimeout(() => {
              autoExpand(e)
            }, 0)
          }}
          onFocus={(e) => {
            setFocus(true)
            setTimeout(() => {
              autoExpand(e)
            }, 0)
            isMobile && setShowAttachmentIcons(false)
          }}
          onBlur={() => {
            selectedChannel.endTyping()
            setFocus(false)
            setShowAttachmentIcons(true)
          }}
          rows="1"
          placeholder={previewingImages ? 'Kommentera eller skicka' : 'Skriv ditt meddelande'}
          maxLength="4000"
        />
      </TextAreaWrapper>
      <IconContainer>
        {showSendButton && (
          <IconSendButton
            data-testid="send-button"
            htmlFor="sendInput"
            tabIndex="0"
            role="button"
            aria-label="Skicka meddelande"
            onKeyDown={(e) => onEnterKeyEvent(e, validateMessage)}
            onClick={validateMessage}
            disabled={isSending}
          >
            <IconSendFill alt="skicka meddelande" width={32} height={24} aria-hidden="true" />
          </IconSendButton>
        )}
      </IconContainer>
    </Wrapper>
  )
}

InputField.propTypes = {
  // Input value.
  input: PropTypes.string.isRequired,
  // Validates message.
  validateMessage: PropTypes.func,
  // Handles file uploading.
  onUploadFileChange: PropTypes.func,
  handleUploadRefs: PropTypes.func,
  // File ref.
  fileUploaderForwardRef: PropTypes.shape({ current: PropTypes.object }),
  // Image ref.
  imageUploaderForwardRef: PropTypes.shape({ current: PropTypes.object }),
  // Input ref.
  textInputForwardRef: PropTypes.shape({ current: PropTypes.object }),
  // Current user selected channel (https://docs.sendbird.com/platform/group_channel#_4_resource_representation).
  selectedChannel: PropTypes.object,
  setInput: PropTypes.func,
  sessionStorageKey: PropTypes.string,
  previewImages: PropTypes.array,
  isMobile: PropTypes.bool,
  isSending: PropTypes.bool,
}

export default React.memo(InputField)
